import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent, CartUpdateEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';
import { sectionRenderer, morphSection } from '@theme/section-renderer';

/**
 * Collects the section IDs that render cart contents.
 * @returns {string[]}
 */
function cartSectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
    const id = /** @type {HTMLElement} */ (el).dataset.sectionId;
    if (id) ids.add(id);
  });
  return Array.from(ids);
}

/**
 * Builds the payload additions that ask the Cart AJAX API to return re-rendered sections.
 * Rendering them inside the cart request keeps them in the same market/localization context
 * as the mutation itself, instead of relying on a separate section rendering GET.
 * @returns {{sections: string, sections_url: string}}
 */
function cartSectionsPayload() {
  return {
    sections: cartSectionIds().join(','),
    sections_url: window.location.pathname,
  };
}

/**
 * Morphs section HTML returned by the Cart AJAX API.
 * @param {Record<string, string> | undefined} sections
 * @returns {boolean} whether at least one section was applied
 */
function applyCartSections(sections) {
  if (!sections) return false;

  let applied = false;
  for (const [id, html] of Object.entries(sections)) {
    if (typeof html !== 'string' || !html) continue;
    try {
      morphSection(id, html);
      applied = true;
    } catch {
      // Section is not on this page — ignore and let the caller fall back if nothing applied.
    }
  }
  return applied;
}

/**
 * Fallback refresh through the Section Rendering API, used when a cart response
 * came back without sections.
 */
function refreshCartSections() {
  cartSectionIds().forEach((id) => sectionRenderer.renderSection(id, { cache: false }));
}

document.addEventListener('click', async (e) => {
  const btn = e.target.closest('.js-cart-qty');
  if (!btn) return;
  e.preventDefault();
  e.stopPropagation();

  const key = btn.dataset.key;
  const qty = parseInt(btn.dataset.qty, 10);

  if (!key || isNaN(qty)) return;

  const control = btn.closest('.fcb-cart-item__qty-control');
  if (control) control.querySelectorAll('button').forEach((b) => { b.disabled = true; });

  // COLL_CARD items have _unique_id — clicking + must add a new separate line
  // item with a fresh unique_id instead of incrementing the existing line quantity.
  const uniqueId = btn.dataset.uniqueId;
  const variantId = btn.dataset.variantId;
  if (uniqueId && variantId) {
    try {
      const newUniqueId = 'CARD-' + Date.now() + '-' + Math.random().toString(36).substring(7);
      /** @type {Record<string, string>} */
      const properties = { _unique_id: newUniqueId };
      const isReturnable = btn.dataset.isReturnable;
      if (isReturnable !== undefined && isReturnable !== '') {
        properties._is_returnable = isReturnable === 'true';
      }
      const r = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: Number(variantId),
          quantity: 1,
          properties,
          ...cartSectionsPayload(),
        }),
      });
      if (!r.ok) throw new Error();
      const data = await r.json();
      if (!applyCartSections(data.sections)) refreshCartSections();
    } catch {
      location.reload();
    } finally {
      if (control) control.querySelectorAll('button').forEach((b) => { b.disabled = false; });
    }
    return;
  }

  /** @type {Record<string, number>} */
  const updates = { [key]: qty };

  const mergeKeys = (btn.dataset.mergeKeys || '').split(',').map((k) => k.trim()).filter(Boolean);
  mergeKeys.forEach((mk) => {
    if (mk !== key) updates[mk] = qty;
  });

  try {
    const r = await fetch(Theme.routes.cart_update_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates, ...cartSectionsPayload() }),
    });
    if (!r.ok) throw new Error();
    const data = await r.json();
    if (!applyCartSections(data.sections)) refreshCartSections();
  } catch {
    location.reload();
  } finally {
    if (control) control.querySelectorAll('button').forEach((b) => { b.disabled = false; });
  }
});

document.addEventListener('click', async (e) => {
  const btn = e.target.closest('.js-cart-remove');
  if (!btn) return;
  e.preventDefault();
  e.stopPropagation();
  const keys = (btn.dataset.removeKeys || '').split(',').map((k) => k.trim()).filter(Boolean);
  if (!keys.length) return;
  const updates = {};
  keys.forEach((key) => { updates[key] = 0; });
  try {
    const res = await fetch(Theme.routes.cart_update_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates, ...cartSectionsPayload() }),
    });
    if (!res.ok) throw new Error();
    const data = await res.json();
    if (!applyCartSections(data.sections)) refreshCartSections();
  } catch {
    location.reload();
  }
});

window.cartDrawerAddItem = async (variantId, sourceElement) => {
  if (sourceElement) sourceElement.disabled = true;
  try {
    const properties = {};
    const isReturnable = sourceElement?.dataset.isReturnable;
    if (isReturnable !== undefined && isReturnable !== '') {
      properties._is_returnable = isReturnable === 'true';
    }
    const res = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        id: variantId,
        quantity: 1,
        ...(Object.keys(properties).length ? { properties } : {}),
        ...cartSectionsPayload(),
      }),
    });
    if (!res.ok) throw new Error();
    const data = await res.json();
    if (!applyCartSections(data.sections)) refreshCartSections();
  } catch {
    location.reload();
  } finally {
    if (sourceElement) sourceElement.disabled = false;
  }
};

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      refreshCartSections();
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
